/**
 * Startup prompt when global notifications are off (见 23).
 *
 * Painted on the sidebar column by the workbench, not by a module. The host
 * decides when the card is up; this file only projects that snapshot.
 *
 * First launch (Figma `10229:60138` / `10229:60305`): 240×242 card, bottom-left.
 * Its left edge sits at the rail's collapsed width (`activityBar.minSize`).
 * Fill `#f5f5f5` (`--app-color-bg-module-base`). Glows are the exported
 * ellipses. The frame also marks `backdrop-blur(10px)`; new surfaces on macOS
 * do not use backdrop-filter, and this fill is opaque.
 *
 * The host shows this card on each open of a day whose check found
 * notifications off. The banner frame (Figma `10229:60317` / `10229:60472`)
 * stays in this file; the host does not select it.
 *
 * Enter and leave are opacity plus a short translate. The banner travels less
 * than the card, and leaving is shorter than arriving. `prefers-reduced-motion`
 * snaps to the resting state.
 */
import './notification-prompt.css';

import type { ChannelClient } from '@hibro/platform-core';
import {
  NOTIFICATION_PROMPT_CHANGED_EVENT,
  NOTIFICATION_PROMPT_DISMISS_COMMAND,
  NOTIFICATION_PROMPT_OPEN_COMMAND,
  NOTIFICATION_PROMPT_PERMISSION_COMMAND,
  NOTIFICATION_PROMPT_READ_COMMAND,
  NOTIFICATION_PROMPT_RESOLVE_COMMAND,
  NOTIFICATION_RESUME_COMMAND,
  type NotificationPromptSnapshot,
  type NotificationPromptSurface,
} from '@hibro/shared';
import { Button, Icon } from '@hibro/ui-client';
import {
  type AnimationEvent,
  type ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useT } from '../i18n/i18n';
import { AREA_SPECS } from '../layout/dockview/area-specs';

const bellUrl = new URL('./notification-prompt-bell.png', import.meta.url).href;

const PROMPT_BUTTON =
  'h-8 rounded-full px-2.5 text-[14px] leading-[22px] font-normal hover:bg-(--app-color-accent)';

function reducedMotion(): boolean {
  return (
    typeof matchMedia === 'function' &&
    matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function logPrompt(
  message: string,
  err?: unknown,
  extra?: { level?: 'info' | 'error'; data?: Record<string, unknown> },
): void {
  const reason = err instanceof Error ? err.message : undefined;
  const hibroLog = (
    globalThis as {
      hibroLog?: {
        append: (entry: {
          level: string;
          kind: string;
          message: string;
          data?: Record<string, unknown>;
          error?: { message: string };
        }) => void;
      };
    }
  ).hibroLog;
  hibroLog?.append({
    level: extra?.level ?? 'error',
    kind: 'log',
    message,
    ...(extra?.data ? { data: extra.data } : {}),
    ...(reason ? { error: { message: reason } } : {}),
  });
}

function parseSnapshot(value: unknown): NotificationPromptSnapshot | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return undefined;
  }
  const surface = (value as NotificationPromptSnapshot).surface;
  if (surface !== 'popup' && surface !== 'banner' && surface !== 'none') {
    return undefined;
  }
  const permission = (value as NotificationPromptSnapshot).webPermission;
  const systemBlock = (value as NotificationPromptSnapshot).systemBlock;
  const snapshot: NotificationPromptSnapshot = { surface };
  if (permission === 'default' || permission === 'denied') {
    return {
      ...snapshot,
      webPermission: permission,
      ...(systemBlock === 'denied' || systemBlock === 'not-determined'
        ? { systemBlock }
        : {}),
    };
  }
  if (systemBlock === 'denied' || systemBlock === 'not-determined') {
    return { surface, systemBlock };
  }
  return { surface };
}

function notificationBridge():
  | {
      openSystemSettings?: () => Promise<void>;
      requestAuthorization?: () => Promise<unknown>;
    }
  | undefined {
  return (
    globalThis as {
      hibroNotification?: {
        openSystemSettings?: () => Promise<void>;
        requestAuthorization?: () => Promise<unknown>;
      };
    }
  ).hibroNotification;
}

function desktopNotificationBridge(): boolean {
  return typeof notificationBridge()?.openSystemSettings === 'function';
}

function openSystemNotificationSettings(): Promise<void> {
  const open = notificationBridge()?.openSystemSettings;
  if (typeof open !== 'function') {
    return Promise.reject(
      new Error('system notification settings cannot be opened'),
    );
  }
  return open();
}

function requestSystemAuthorization(): Promise<unknown> {
  const request = notificationBridge()?.requestAuthorization;
  if (typeof request !== 'function') {
    return Promise.reject(
      new Error('system notification authorization cannot be requested'),
    );
  }
  return request();
}

/** Web only. Desktop authorization arrives from the main process. */
function watchWebPermission(
  report: (permission: NotificationPermission) => void,
): () => void {
  if (typeof Notification === 'undefined') {
    logPrompt('notification permission api is missing');
    return () => {};
  }
  report(Notification.permission);
  let stopped = false;
  let remove = (): void => {};
  const permissions = navigator.permissions;
  if (permissions?.query) {
    void permissions
      .query({ name: 'notifications' })
      .then((status) => {
        if (stopped) {
          return;
        }
        const onChange = (): void => {
          report(Notification.permission);
        };
        status.addEventListener('change', onChange);
        remove = () => status.removeEventListener('change', onChange);
      })
      .catch((err: unknown) => {
        logPrompt('notification permission query failed', err);
      });
  }
  return () => {
    stopped = true;
    remove();
  };
}

function useNotificationPrompt(
  client: ChannelClient,
  executeCommand: (command: string, ...args: unknown[]) => Promise<unknown>,
): NotificationPromptSnapshot {
  const [snapshot, setSnapshot] = useState<NotificationPromptSnapshot>({
    surface: 'none',
  });
  const executeRef = useRef(executeCommand);
  executeRef.current = executeCommand;
  const logged = useRef('');

  useEffect(() => {
    let alive = true;
    let fromEvent = false;
    const apply = (value: unknown, fromBroadcast: boolean): void => {
      const next = parseSnapshot(value);
      if (next === undefined) {
        logPrompt(
          fromBroadcast
            ? 'notification prompt event is unreadable'
            : 'notification prompt snapshot is unreadable',
        );
        return;
      }
      if (fromBroadcast) {
        fromEvent = true;
      }
      if (alive && (fromBroadcast || !fromEvent)) {
        const key = `${next.surface}/${next.systemBlock ?? ''}/${next.webPermission ?? ''}`;
        if (key !== logged.current) {
          logged.current = key;
          logPrompt('notification prompt painted', undefined, {
            level: 'info',
            data: {
              surface: next.surface,
              systemBlock: next.systemBlock ?? 'none',
              webPermission: next.webPermission ?? 'none',
            },
          });
        }
        setSnapshot(next);
      }
    };
    const dispose = client.listen(NOTIFICATION_PROMPT_CHANGED_EVENT, (data) => {
      apply(data, true);
    });
    void executeRef
      .current(NOTIFICATION_PROMPT_OPEN_COMMAND)
      .catch((err: unknown) => {
        logPrompt('notification prompt open failed', err);
      });
    void executeRef
      .current(NOTIFICATION_PROMPT_READ_COMMAND)
      .then((value) => {
        if (!alive) {
          return;
        }
        apply(value, false);
      })
      .catch((err: unknown) => {
        logPrompt('notification prompt read failed', err);
      });
    const stopWatch = desktopNotificationBridge()
      ? () => {}
      : watchWebPermission((permission) => {
          void executeRef
            .current(NOTIFICATION_PROMPT_PERMISSION_COMMAND, permission)
            .catch((err: unknown) => {
              logPrompt('notification prompt permission report failed', err);
            });
        });
    return () => {
      alive = false;
      dispose();
      stopWatch();
    };
  }, [client]);

  return snapshot;
}

function PromptMotion({
  shown,
  enterClass,
  exitClass,
  children,
}: {
  shown: boolean;
  enterClass: string;
  exitClass: string;
  children: (
    motionClass: string | undefined,
    onAnimationEnd: (event: AnimationEvent<HTMLElement>) => void,
  ) => ReactElement;
}): ReactElement | null {
  const [present, setPresent] = useState(false);
  const [phase, setPhase] = useState<'enter' | 'exit' | 'idle'>('idle');
  const presentRef = useRef(false);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  useEffect(() => {
    if (reducedMotion()) {
      presentRef.current = shown;
      setPresent(shown);
      setPhase('idle');
      return;
    }
    if (shown) {
      presentRef.current = true;
      setPresent(true);
      setPhase('enter');
      return;
    }
    if (!presentRef.current) {
      return;
    }
    setPhase('exit');
  }, [shown]);

  if (!present) {
    return null;
  }

  const motionClass =
    phase === 'enter' ? enterClass : phase === 'exit' ? exitClass : undefined;

  const onAnimationEnd = (event: AnimationEvent<HTMLElement>): void => {
    if (event.target !== event.currentTarget) {
      return;
    }
    const leaving =
      event.animationName === 'notification-prompt-rise-out' ||
      event.animationName === 'notification-prompt-drop-out';
    if (phaseRef.current === 'exit' && leaving) {
      presentRef.current = false;
      setPresent(false);
      setPhase('idle');
      return;
    }
    if (phaseRef.current === 'enter') {
      setPhase('idle');
    }
  };

  return children(motionClass, onAnimationEnd);
}

function TurnOnButton({
  label,
  busy,
  onClick,
  icon,
  className,
}: {
  label: string;
  busy: boolean;
  onClick: () => void;
  icon?: boolean;
  className?: string;
}): ReactElement {
  return (
    <Button disabled={busy} className={className} onClick={onClick}>
      {icon ? (
        <Icon
          spec={{ type: 'builtin', id: 'i-read-notification' }}
          size={18}
          className="size-[18px] text-(--app-color-on-accent)"
        />
      ) : null}
      {label}
    </Button>
  );
}

export function NotificationPrompt({
  client,
  executeCommand,
}: {
  client: ChannelClient;
  executeCommand: (command: string, ...args: unknown[]) => Promise<unknown>;
}): ReactElement | null {
  const t = useT();
  const snapshot = useNotificationPrompt(client, executeCommand);
  const surface = snapshot.surface;
  const [busy, setBusy] = useState(false);
  const held = useRef<Exclude<NotificationPromptSurface, 'none'>>('popup');
  if (surface !== 'none') {
    held.current = surface;
  }
  const kind = surface === 'none' ? held.current : surface;
  const askPermission = snapshot.webPermission === 'default';
  const permissionBlocked = snapshot.webPermission === 'denied';
  const systemBlock = snapshot.systemBlock;

  const dismiss = (): void => {
    void executeCommand(NOTIFICATION_PROMPT_DISMISS_COMMAND).catch(
      (err: unknown) => {
        logPrompt('notification prompt dismiss failed', err);
      },
    );
  };

  const enable = (): void => {
    const action = busy
      ? 'busy'
      : permissionBlocked
        ? 'web-denied'
        : askPermission
          ? 'request-web-permission'
          : systemBlock === 'not-determined'
            ? 'request-system-authorization'
            : systemBlock === 'denied'
              ? 'open-system-settings'
              : 'resume-dnd';
    logPrompt('notification prompt enable', undefined, {
      level: 'info',
      data: {
        action,
        systemBlock: systemBlock ?? 'none',
        webPermission: snapshot.webPermission ?? 'none',
      },
    });
    if (busy || permissionBlocked) {
      return;
    }
    setBusy(true);
    const resume = (): Promise<unknown> =>
      executeCommand(NOTIFICATION_RESUME_COMMAND);
    const close = (): Promise<unknown> =>
      executeCommand(NOTIFICATION_PROMPT_DISMISS_COMMAND);
    // Either button closes the card on click. 「开启」then still asks for
    // permission or opens system settings; a later grant retires the day as on.
    const run = close().then(() => {
      if (askPermission) {
        return Notification.requestPermission().then((permission) =>
          permission === 'granted'
            ? executeCommand(NOTIFICATION_PROMPT_RESOLVE_COMMAND)
            : undefined,
        );
      }
      if (systemBlock === 'not-determined') {
        return requestSystemAuthorization().then((access) =>
          resume().then(() =>
            executeCommand(NOTIFICATION_PROMPT_RESOLVE_COMMAND, access),
          ),
        );
      }
      if (systemBlock === 'denied') {
        return openSystemNotificationSettings().then(() => resume());
      }
      return resume().then(() =>
        executeCommand(NOTIFICATION_PROMPT_RESOLVE_COMMAND),
      );
    });
    void run
      .catch((err: unknown) => {
        logPrompt('notification prompt enable failed', err);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const title = t('notificationPrompt.title');
  const body = permissionBlocked
    ? t('settings.osNotifications.denied')
    : systemBlock === 'denied'
      ? t('notificationPrompt.systemDenied')
      : systemBlock === 'not-determined'
        ? t('notificationPrompt.systemNotDetermined')
        : t('notificationPrompt.body');
  const turnOn = t('notificationPrompt.turnOn');
  const card =
    kind === 'banner' ? (
      <div className="px-1 pt-1">
        <div className="relative flex flex-col gap-2 rounded-[12px] bg-(--app-color-card) px-3 py-2.5 shadow-[0_6px_13px_rgba(0,0,0,0.12)]">
          <button
            type="button"
            className="absolute top-2 right-2 flex size-[18px] items-center justify-center text-(--app-color-text-tertiary)"
            aria-label={t('notificationPrompt.dismiss')}
            onClick={dismiss}
          >
            <Icon
              spec={{ type: 'builtin', id: 'close' }}
              size={18}
              className="size-[18px]"
            />
          </button>
          <div className="pr-6">
            <p className="text-[14px] leading-[22px] text-(--app-color-text-primary)">
              {title}
            </p>
            <p className="text-[11px] leading-4 text-(--app-color-text-secondary)">
              {body}
            </p>
          </div>
          {permissionBlocked ? null : (
            <TurnOnButton
              label={turnOn}
              busy={busy}
              icon
              onClick={enable}
              className={`w-fit gap-1 ${PROMPT_BUTTON} bg-(--app-color-accent) text-(--app-color-on-accent)`}
            />
          )}
        </div>
      </div>
    ) : (
      <div className="pointer-events-auto relative flex h-[242px] flex-col items-center justify-end gap-5 overflow-hidden rounded-[12px] bg-(--app-color-bg-module-base) px-1 pt-1 pb-3 shadow-[0px_4px_20px_0px_rgba(0,0,0,0.15)]">
        <div
          aria-hidden
          className="pointer-events-none absolute h-[209px] w-[209px]"
          style={{ left: -53, top: -126 }}
        >
          <img
            alt=""
            src={
              new URL('./notification-prompt-glow-left.svg', import.meta.url)
                .href
            }
            className="absolute max-w-none"
            style={{
              left: '-47.85%',
              top: '-47.85%',
              width: '195.7%',
              height: '195.7%',
            }}
          />
        </div>
        <div
          aria-hidden
          className="pointer-events-none absolute h-[196px] w-[196px]"
          style={{ left: 120, top: -107 }}
        >
          <img
            alt=""
            src={
              new URL('./notification-prompt-glow-right.svg', import.meta.url)
                .href
            }
            className="absolute max-w-none"
            style={{
              left: '-51.02%',
              top: '-51.02%',
              width: '202.04%',
              height: '202.04%',
            }}
          />
        </div>
        <div
          aria-hidden
          className="pointer-events-none absolute top-5 left-[73px] h-[86px] w-[83px] overflow-hidden"
        >
          <img
            alt=""
            src={bellUrl}
            className="absolute top-0 left-0 h-[107.88%] w-full max-w-none"
          />
        </div>
        <div className="relative flex w-full flex-col items-center gap-1 px-2.5 text-center">
          <p className="text-[16px] leading-6 font-semibold text-(--app-color-text-primary)">
            {title}
          </p>
          <p className="text-[12px] leading-[18px] text-(--app-color-text-secondary)">
            {body}
          </p>
        </div>
        <div className="relative flex w-full gap-2 px-2.5">
          <Button
            variant="outline"
            className="h-8 flex-1 rounded-full border-(--app-color-border) bg-(--app-color-card) px-2.5 text-[14px] leading-[22px] font-normal text-(--app-color-text-primary) hover:bg-(--app-color-card)"
            onClick={dismiss}
          >
            {t('notificationPrompt.cancel')}
          </Button>
          {permissionBlocked ? null : (
            <TurnOnButton
              label={turnOn}
              busy={busy}
              onClick={enable}
              className={`flex-1 ${PROMPT_BUTTON} bg-(--app-color-accent) text-(--app-color-on-accent)`}
            />
          )}
        </div>
      </div>
    );

  return (
    <PromptMotion
      shown={surface !== 'none'}
      enterClass={
        kind === 'banner'
          ? 'notification-prompt-drop-in'
          : 'notification-prompt-rise-in'
      }
      exitClass={
        kind === 'banner'
          ? 'notification-prompt-drop-out'
          : 'notification-prompt-rise-out'
      }
    >
      {(motionClass, onAnimationEnd) =>
        kind === 'banner' ? (
          <div
            role="region"
            aria-label={title}
            className={motionClass}
            onAnimationEnd={onAnimationEnd}
          >
            {card}
          </div>
        ) : (
          <div
            role="region"
            aria-label={title}
            className={[
              'pointer-events-none absolute bottom-3 z-20 w-[240px]',
              motionClass,
            ]
              .filter(Boolean)
              .join(' ')}
            style={{ left: AREA_SPECS.activityBar.minSize }}
            onAnimationEnd={onAnimationEnd}
          >
            {card}
          </div>
        )
      }
    </PromptMotion>
  );
}
